import React from 'react';
import { Chip } from '@mui/material';
import { TaskStatus } from '@/types';

interface StatusBadgeProps {
  status: TaskStatus;
  size?: 'small' | 'medium';
}

const statusConfig: Record<TaskStatus, { label: string; color: string; bgcolor: string }> = {
  [TaskStatus.NOT_STARTED]: {
    label: 'Not Started',
    color: '#64748b', // Gray - Industry standard for "to do"
    bgcolor: '#f1f5f9',
  },
  [TaskStatus.IN_PROGRESS]: {
    label: 'In Progress',
    color: '#3b82f6', // Blue - Industry standard for "in progress" (Jira, Asana)
    bgcolor: '#dbeafe',
  },
  [TaskStatus.BLOCKED]: {
    label: 'Blocked',
    color: '#dc2626', // Red - Industry standard for "blocked/stuck" (critical issue)
    bgcolor: '#fee2e2',
  },
  [TaskStatus.COMPLETED]: {
    label: 'Completed',
    color: '#16a34a', // Green - Universal standard for "done/completed"
    bgcolor: '#dcfce7',
  },
  [TaskStatus.CANCELLED]: {
    label: 'Cancelled',
    color: '#6b7280', // Gray - Industry standard for "cancelled" (neutral/closed)
    bgcolor: '#f3f4f6',
  },
};

/**
 * StatusBadge Component
 * Visual indicator for task status with consistent colors across the app
 */
const StatusBadge: React.FC<StatusBadgeProps> = ({ status, size = 'small' }) => {
  const config = statusConfig[status] || {
    label: status || 'Unknown',
    color: '#64748b',
    bgcolor: '#f1f5f9',
  };

  return (
    <Chip
      label={config.label}
      size={size}
      sx={{
        height: size === 'small' ? 24 : 28,
        fontSize: size === 'small' ? '0.75rem' : '0.8125rem',
        fontWeight: 500,
        color: config.color,
        bgcolor: config.bgcolor,
        '& .MuiChip-label': {
          px: 1.5,
        },
      }}
    />
  );
};

export default StatusBadge;
